import React, { useState, useEffect } from 'react';
import { useMercenary } from '../../../contexts/MercenaryContext';
import axios from 'axios';
import styles from './VaultPage.module.css';
import Toast from '../../Shared/Toast';
import Tooltip from '../ItemTooltip';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';

interface VaultItem {
  id: number;
  name: string;
  quantity: number;
  item?: any;
}

const VaultPage = () => {
  const { mercenaryId } = useMercenary();
  const [vault, setVault] = useState<{ silver: number; items: VaultItem[] } | null>(null);
  const [inventory, setInventory] = useState<VaultItem[]>([]);
  const [silver, setSilver] = useState(0);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [itemQuantity, setItemQuantity] = useState<number>(1);
  const [actionType, setActionType] = useState<'deposit' | 'withdraw'>('deposit');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [vaultStatus, setVaultStatus] = useState<{
    isBuilt: boolean; buildingLevel: number;
    silverCapacity: number; itemSlots: number; dailyUses: number; usesToday: number;
  } | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const fetchVault = async () => {
    try {
      const response = await axios.get('/api/vault');
      setVault(response.data);
    } catch (error) {
      console.error(error);
      showToast('Failed to fetch vault details.', 'error');
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get('/api/vault/status');
      setVaultStatus(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get('/api/vault/inventory');
      setInventory(response.data);
    } catch (error) {
      console.error(error);
      showToast('Failed to fetch inventory items.', 'error');
    }
  };

  const handleAction = async () => {
    try {
      const endpoint = `/api/vault/${actionType}`;
      const data: any = { mercenaryId };
      if (silver > 0) data.silver = silver;
      if (selectedItem && itemQuantity > 0) {
        data.items = [{ itemId: selectedItem, quantity: itemQuantity }];
      }

      if (!data.silver && !data.items) {
        showToast('Please specify silver or items to proceed.', 'error');
        return;
      }

      await axios.post(endpoint, data);
      showToast(`${actionType === 'deposit' ? 'Deposit' : 'Withdrawal'} successful!`, 'success');
      fetchVault();
      fetchInventory();
      fetchStatus();
      resetInputs();
    } catch (error: any) {
      console.error(error);
      const message =
        error.response?.data?.message ||
        `Failed to ${actionType}. Please check your input and try again.`;
      showToast(message, 'error');
      fetchStatus();
    }
  };

  const resetInputs = () => {
    setSilver(0);
    setSelectedItem(null);
    setItemQuantity(1);
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  useEffect(() => {
    fetchStatus();
    fetchVault();
    fetchInventory();
  }, []);

  const currentItems = actionType === 'deposit' ? inventory : vault?.items || [];

  // Returns the full item object for image/tooltip use.
  // Vault items have a nested .item; inventory items are the full object themselves.
  const getItemObject = (item: VaultItem) => (item as any).item ?? item;

  if (statusLoading) {
    return <div className={styles.vaultPage} />;
  }

  if (vaultStatus && !vaultStatus.isBuilt) {
    return (
      <div className={styles.vaultPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>The Glacier Vault</h1>
          <p className={styles.description}>
            Deep beneath the frozen stone lies the chamber meant to guard the city’s greatest treasures, but its gates remain unfinished..<br />
            Until the people of Njordheim complete the work, the vault cannot be opened.<br />
            Contribute to the City Projects to help finish the Glacier Vault.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.vaultPage}>
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}
      
      <div className={styles.header}>
        <h1 className={styles.title}>The Glacier Vault</h1>
        <p className={styles.description}>
          Your personal secure storage for silver and items. All your mercenaries share access to this vault, 
          allowing you to safely store valuable items and currency between adventures.
        </p>
        {vaultStatus && (
          <p className={styles.usageLimit}>
            Level {vaultStatus.buildingLevel} - Up to {vaultStatus.silverCapacity.toLocaleString()} silver &amp; {vaultStatus.itemSlots} item stacks
            &nbsp;·&nbsp; {vaultStatus.usesToday} / {vaultStatus.dailyUses} actions used today
          </p>
        )}
      </div>

      <div className={styles.mainContainer}>
        {/* Vault Contents Panel */}
        <div className={styles.vaultPanel}>
          <div className={styles.panelHeader}>
            <h2>Vault Contents</h2>
            <span className={styles.subtitle}>Shared across all your mercenaries</span>
          </div>

          <div className={styles.silverCard}>
            <div className={styles.cardHeader}>
              <h3>Silver</h3>
            </div>
            <div className={styles.silverAmount}>{vault?.silver?.toLocaleString() || 0}</div>
          </div>

          <div className={styles.itemsCard}>
            <div className={styles.cardHeader}>
              <h3>Stored Items</h3>
            </div>
            <div className={styles.itemsList}>
              {vault?.items && vault.items.length > 0 ? (
                vault.items.map((item) => (
                  <Tooltip key={item.id} itemId={item.id} item={item.item} style={{ display: 'block' }}>
                    <div className={styles.itemRow}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                          src={getItemImageSrc(item.item)}
                          alt={item.name}
                          className={styles.itemIcon}
                          onError={handleImgError}
                        />
                        <span className={styles.itemName}>{item.name}</span>
                      </div>
                      <span className={styles.itemQuantity}>x{item.quantity}</span>
                    </div>
                  </Tooltip>
                ))
              ) : (
                <p className={styles.emptyMessage}>No items stored</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions Panel */}
        <div className={styles.actionsPanel}>
          <div className={styles.panelHeader}>
            <h2>Current Mercenary</h2>
            <span className={styles.subtitle}>Inventory & Actions</span>
          </div>

          {/* Tab Switcher */}
          <div className={styles.tabBar}>
            <button
              className={`${styles.tab} ${actionType === 'deposit' ? styles.activeTab : ''}`}
              onClick={() => {
                setActionType('deposit');
                resetInputs();
              }}
            >
              Deposit
            </button>
            <button
              className={`${styles.tab} ${actionType === 'withdraw' ? styles.activeTab : ''}`}
              onClick={() => {
                setActionType('withdraw');
                resetInputs();
              }}
            >
              Withdraw
            </button>
          </div>

          <div className={styles.actionContent}>
            {actionType === 'deposit' && (
              <div className={styles.actionInfo}>
                <p>Deposit silver and items from your mercenary's inventory into the vault for safekeeping.</p>
              </div>
            )}
            {actionType === 'withdraw' && (
              <div className={styles.actionInfo}>
                <p>Withdraw silver and items from the vault to your mercenary's inventory.</p>
              </div>
            )}

            {/* Silver Input */}
            <div className={styles.inputSection}>
              <label className={styles.inputLabel}>Silver Amount</label>
              <input
                type="number"
                className={styles.input}
                value={silver || ''}
                onChange={(e) => setSilver(parseInt(e.target.value, 10) || 0)}
                placeholder="Enter amount"
                min="0"
              />
            </div>

            {/* Item Selection */}
            <div className={styles.inputSection}>
              <label className={styles.inputLabel}>
                {actionType === 'deposit' ? 'Select from Inventory' : 'Select from Vault'}
              </label>
              <div className={styles.itemSelectionList}>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <Tooltip key={item.id} itemId={item.id} item={getItemObject(item)} style={{ display: 'block' }}>
                      <div
                        className={`${styles.selectableItem} ${selectedItem === item.id ? styles.selectedItem : ''}`}
                        onClick={() => {
                          setSelectedItem(item.id);
                          setItemQuantity(1);
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img
                            src={getItemImageSrc(getItemObject(item))}
                            alt={item.name}
                            className={styles.itemIcon}
                            onError={handleImgError}
                          />
                          <span className={styles.selectableItemName}>{item.name}</span>
                        </div>
                        <span className={styles.selectableItemQty}>x{item.quantity}</span>
                      </div>
                    </Tooltip>
                  ))
                ) : (
                  <p className={styles.emptyMessage}>No items available</p>
                )}
              </div>

              {selectedItem && (() => {
                const selectedItemDetails = currentItems.find((item) => item.id === selectedItem);
                return selectedItemDetails?.quantity !== undefined && selectedItemDetails.quantity > 1;
              })() && (
                <div className={styles.quantitySelector}>
                  <label className={styles.quantityLabel}>Quantity:</label>
                  <select
                    className={styles.quantitySelect}
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(parseInt(e.target.value, 10))}
                  >
                    {Array.from(
                      {
                        length:
                          currentItems.find((item) => item.id === selectedItem)?.quantity || 1,
                      },
                      (_, i) => i + 1
                    ).map((qty) => (
                      <option key={qty} value={qty}>
                        {qty}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Action Button */}
            <button
              className={styles.actionButton}
              onClick={handleAction}
              disabled={!silver && !selectedItem}
            >
              {actionType === 'deposit' ? 'Deposit' : 'Withdraw'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultPage;
